import {
  useGetQuery,
  useLazyGetQuery,
  usePostMutation,
  usePutMutation,
} from "slices/api";
import {
  BILLING_CALCULATIONS,
  BILLING_CYCLE,
  CONSUMER_BILLING,
  UNPAID_BILLS,
  DISPATCHED_BILL,
  POWER_ABSTRACT,
  MISSING_READINGS,
} from "tagTypes";

export const useGetBillingCalculationQuery = (id, query = {}) =>
  useGetQuery({
    endpoint: `/billing-cycle/${id}/bill-calculation`,
    query,
    tags: [BILLING_CALCULATIONS],
  });

export const useLazyUnpaidBillsQuery = () => {
  const [getUnpaidBills, data] = useLazyGetQuery();
  const unPaidBills = async (query = {}) =>
    await getUnpaidBills(
      {
        endpoint: "/bill-calculation/unpaid-bill",
        query,
        tags: [UNPAID_BILLS],
      },
      true
    ).unwrap();
  return [unPaidBills, data];
};

export const useLazyGetTemporaryDisconnectedConsumersQuery = () => {
  const [getDisconnectedConsumers, data] = useLazyGetQuery();
  const disconnectedConsumers = async (query = {}) =>
    await getDisconnectedConsumers(
      {
        endpoint:
          "bill-calculation/temporary-disconnected-connection-paid-bill",
        query,
        tags: [UNPAID_BILLS],
      },
      true
    ).unwrap();
  return [disconnectedConsumers, data];
};

export const useLazyBillingCalculationQuery = () => {
  const [getBillingCalculations, data] = useLazyGetQuery();
  const billingCalculations = async (id, query) =>
    await getBillingCalculations(
      {
        endpoint: `/billing-cycle/${id}/bill-calculation`,
        query,
        tags: [BILLING_CYCLE, BILLING_CALCULATIONS],
      },
      true
    ).unwrap();
  return [billingCalculations, data];
};

export const useLazyBillingCalculationReportQuery = () => {
  const [getBillingCalculationReportData, data] = useLazyGetQuery();
  const fetchBillingCalculations = async (billingCycleId, query = {}) =>
    await getBillingCalculationReportData(
      {
        endpoint: `/report/billing-cycle/${billingCycleId}/bill-calculation`,
        query,
        tags: [BILLING_CYCLE, BILLING_CALCULATIONS],
      },
      true
    ).unwrap();
  return [fetchBillingCalculations, data];
};

export const useLazyDispatchedBillReportQuery = () => {
  const [getDispatchedBillReportData, data] = useLazyGetQuery();
  const fetchDispatchedBills = async (query = {}) =>
    await getDispatchedBillReportData(
      {
        endpoint: "/report/dispatched-bill-report",
        query,
        tags: [DISPATCHED_BILL],
      },
      true
    ).unwrap();
  return [fetchDispatchedBills, data];
};

export const useLazyGetAbstractNoticeReportQuery = () => {
  const [getAbstractNotice, data] = useLazyGetQuery();
  const getAbstractNoticeReport = async (query = {}) =>
    await getAbstractNotice(
      {
        endpoint: "/report/outstanding-bill-notice-report",
        query,
        tags: [DISPATCHED_BILL],
      },
      true
    ).unwrap();
  return [getAbstractNoticeReport, data];
};

export const useLazyGetBillingCycleChartQuery = () => {
  const [getBillingCycleChartData, data] = useLazyGetQuery();
  const getBillApprovalChart = async (id, query = {}) =>
    await getBillingCycleChartData(
      {
        endpoint: `/bill-calculation/${id}/approval-tracking`,
        query,
        tags: [BILLING_CYCLE, BILLING_CYCLE],
      },
      true
    ).unwrap();
  return [getBillApprovalChart, data];
};

export const useLazyGetMissingReadingsQuery = () => {
  const [getMissingReadings, data] = useLazyGetQuery();
  const missingReadingsData = async (query = {}) =>
    await getMissingReadings(
      {
        endpoint: "/report/missing-meter-reading",
        query,
        tags: [MISSING_READINGS],
      },
      true
    ).unwrap();
  return [missingReadingsData, data];
};

export const usePostGenerateBillsMutation = () => {
  const [generateBills, data] = usePostMutation();
  const addBillingCalculations = async (billingCycleId, query = {},payload=[]) =>
    await generateBills({
      endpoint: `/billing-cycle/${billingCycleId}/calculate-bill`,
      query,
      payload,
      toastMessage:
        "You will be notified once all bill calculations are generated",
      tags: [BILLING_CYCLE],
    }).unwrap();
  return [addBillingCalculations, data];
};

export const usePostDispatchBillsMutation = () => {
  const [dispatchBills, data] = usePostMutation();
  const setBillsPdf = async (billingCycleId, payload) =>
    await dispatchBills({
      endpoint: `/billing-cycle/${billingCycleId}/dispatch-bill`,
      payload: payload || [],
      toastMessage: "Bill calculations pdf send successfully",
      tags: [BILLING_CALCULATIONS],
    }).unwrap();
  return [setBillsPdf, data];
};

export const usePutBillingCalculation = () => {
  const [updateBillCalculationsStatus, data] = usePutMutation();
  const updateBillingStatus = async (payload, toastMessage) =>
    await updateBillCalculationsStatus({
      endpoint: "/bill-calculation/status",
      payload: payload || {},
      toastMessage,
      tags: [BILLING_CALCULATIONS],
    }).unwrap();
  return [updateBillingStatus, data];
};

export const usePostGenerateInvoiceBillingMutation = () => {
  const [invoiceGenerate, data] = usePostMutation();
  const generateInvoice = async (billingCycleId, payload = []) =>
    await invoiceGenerate({
      endpoint: `/billing-cycle/${billingCycleId}/generate-bill-einvoice`,
      payload,
      toastMessage: "You will be notified once all e-invoices are generated",
      tags: [BILLING_CALCULATIONS],
    }).unwrap();
  return [generateInvoice, data];
};

export const usePostElectricityBillPaymentMutation = () => {
  const [dispatchBills, data] = usePostMutation();
  const setBillsPdf = async (payload = {}) =>
    await dispatchBills({
      endpoint: "/bill-calculation/bill-payment",
      payload,
      tags: [BILLING_CALCULATIONS],
    }).unwrap();
  return [setBillsPdf, data];
};

export const useBillingQuery = () =>
  useGetQuery({
    endpoint: "/bill-calculation/user/unpaid-bill",
    tags: [CONSUMER_BILLING, BILLING_CALCULATIONS],
  });

export const useLazyPowerAbstractReportQuery = () => {
  const [getPowerAbstractReportData, data] = useLazyGetQuery();
  const fetchPowerAbstractData = async (billingCycleId, query = {}) =>
    await getPowerAbstractReportData(
      {
        endpoint: `/report/billing-cycle/${billingCycleId}/electricity-bill-abstract-report`,
        query,
        tags: [POWER_ABSTRACT],
      },
      true
    ).unwrap();
  return [fetchPowerAbstractData, data];
};

export const usePutManualDispatchBilling = () => {
  const [updateManualDispatchBill, data] = usePutMutation();
  const updateManualStatus = async (payload, toastMessage) =>
    await updateManualDispatchBill({
      endpoint: "/bill-calculation/manual-update-status",
      payload: payload || {},
      toastMessage,
      tags: [BILLING_CALCULATIONS],
    }).unwrap();
  return [updateManualStatus, data];
};
